/**
 * Slack Action Builders
 *
 * Creates action definitions for sending entities to Slack.
 * Provides both generic "send to Slack" and "send to sales channel" actions.
 */

import type { DetailActionStrict, BaseEntity } from "@/lib/actions/types";
import { ROLE_COMBINATIONS } from "../../types/action-properties";

/**
 * Create "Send to Slack" action
 * Opens modal to select channel and send entity
 */
export function createSendToSlackAction<T extends BaseEntity>(
  options?: { bulkable?: boolean }
): DetailActionStrict<T> {
  return {
    id: "send-to-slack",
    label: "Send to Slack",
    icon: "send",
    action: "sendToSlack",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: options?.bulkable ?? true,
  };
}

/**
 * Create "Send to Sales Channel" action
 * Sends directly to configured sales channel without modal
 */
export function createSendToSalesChannelAction<T extends BaseEntity>(
  options?: { bulkable?: boolean }
): DetailActionStrict<T> {
  return {
    id: "send-to-sales-channel",
    label: "Send to Sales Channel",
    icon: "share-2",
    action: "sendToSalesChannel",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: options?.bulkable ?? false,
  };
}

/**
 * Create "Open Slack Channel" action.
 *
 * Opens the customer's Slack channel (from `companies.slack_channel_id`) in a
 * new tab so the user can start a conversation about that customer. Non-company
 * entities resolve their company server-side via `/api/slack/channel-link`. When
 * the customer has no channel, the handler shows a "No Slack channel set for
 * this customer" toast rather than opening a dead tab (the action's sync
 * `visible`/`disabled` predicates cannot await the async lookup).
 */
export function createOpenSlackChannelAction<T extends BaseEntity>(
  options?: { bulkable?: boolean }
): DetailActionStrict<T> {
  return {
    id: "open-slack-channel",
    label: "Open Slack Channel",
    icon: "message-square",
    action: "openSlackChannel",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: options?.bulkable ?? false,
    tooltip: "Open this customer's Slack channel to start a conversation.",
  };
}
